import api from "./axios";
import { notifyMintSpaceActivity } from "../utils/mintSpaceRecent";

function spaceIdFromCampaignData(data) {
  if (!data) return null;
  const ms = data.mintSpace;
  if (!ms) return null;
  return typeof ms === "object" ? ms._id || ms.id : ms;
}

function afterCampaignMutation(mintSpaceId, result) {
  if (mintSpaceId && result?.success !== false) {
    notifyMintSpaceActivity(mintSpaceId);
  }
  return result;
}

/**
 * Create a new campaign
 * @param {Object} campaignData - Campaign data
 * @returns {Promise} API response
 */
export const createCampaign = async (campaignData) => {
  const response = await api.post("/campaigns", campaignData);
  const result = response.data;
  const spaceId =
    spaceIdFromCampaignData(campaignData) ||
    spaceIdFromCampaignData(result?.data);
  return afterCampaignMutation(spaceId, result);
};

/**
 * Get all active campaigns (for discover page)
 * @param {Object} filters - Optional filters (status, participantType)
 * @returns {Promise} API response
 */
export const getAllCampaigns = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append("status", filters.status);
  if (filters.participantType) params.append("participantType", filters.participantType);
  if (filters.search) params.append("search", filters.search);
  
  const queryString = params.toString();
  const url = `/campaigns${queryString ? `?${queryString}` : ""}`;
  const response = await api.get(url);
  return response.data;
};

/** Campaigns admins picked for Discover's Featured row, newest pick first. */
export const getFeaturedCampaigns = async () => {
  const response = await api.get("/campaigns/featured");
  return response.data;
};

/**
 * Get all campaigns for the authenticated user
 * @param {string} search - Optional search query
 * @param {string} mintSpaceId - Optional mint space ID to filter by
 * @param {boolean} viewAll - If true and mintSpaceId provided, returns all campaigns for that mint space (not just user's)
 * @returns {Promise} API response
 */
export const getMyCampaigns = async (search = "", mintSpaceId = null, viewAll = false) => {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  if (mintSpaceId) params.append("mintSpace", mintSpaceId);
  if (viewAll) params.append("viewAll", "true");
  
  const queryString = params.toString();
  const url = `/campaigns/my${queryString ? `?${queryString}` : ""}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Get a single campaign by ID
 * @param {string} campaignId - Campaign ID
 * @returns {Promise} API response
 */
export const getCampaign = async (campaignId) => {
  const response = await api.get(`/campaigns/${campaignId}`);
  return response.data;
};

/**
 * Update a campaign
 * @param {string} campaignId - Campaign ID
 * @param {Object} campaignData - Updated campaign data
 * @returns {Promise} API response
 */
export const updateCampaign = async (campaignId, campaignData, mintSpaceId) => {
  const response = await api.put(`/campaigns/${campaignId}`, campaignData);
  const result = response.data;
  const spaceId =
    mintSpaceId ||
    spaceIdFromCampaignData(campaignData) ||
    spaceIdFromCampaignData(result?.data);
  return afterCampaignMutation(spaceId, result);
};

/**
 * Delete a campaign
 * @param {string} campaignId - Campaign ID
 * @returns {Promise} API response
 */
export const deleteCampaign = async (campaignId, mintSpaceId) => {
  const response = await api.delete(`/campaigns/${campaignId}`);
  return afterCampaignMutation(mintSpaceId, response.data);
};

/**
 * Track a campaign view (unique per user)
 * @param {string} campaignId - Campaign ID
 * @returns {Promise} API response
 */
export const trackCampaignView = async (campaignId) => {
  const response = await api.post(`/campaigns/${campaignId}/view`);
  return response.data;
};

/**
 * Stop a campaign (set status to cancelled)
 * @param {string} campaignId - Campaign ID
 * @returns {Promise} API response
 */
export const stopCampaign = async (campaignId, mintSpaceId) => {
  const response = await api.put(`/campaigns/${campaignId}`, { status: "cancelled" });
  return afterCampaignMutation(mintSpaceId, response.data);
};

/**
 * Move amount from the authenticated user's wallet into campaign budget.
 * @param {string} campaignId
 * @param {number} amount
 */
export const fundCampaignFromBalance = async (campaignId, amount, mintSpaceId) => {
  const response = await api.post(`/campaigns/${campaignId}/fund-from-balance`, { amount });
  const result = response.data;
  const spaceId =
    mintSpaceId ||
    spaceIdFromCampaignData(result?.data) ||
    spaceIdFromCampaignData(result?.data?.campaign);
  return afterCampaignMutation(spaceId, result);
};

